import React, { FC } from 'react'
import { useDebounce } from 'react-use'
import compact from 'lodash/compact'
import AutocompleteBase from 'transfix-ui/components/AutocompleteBase'
import { TInputChangeHandler } from 'transfix-ui/components/AutocompleteBase/hooks'
import { useAutocompleteQuery } from 'transfix-ui/components/AutocompleteBase/hooks/useAutocompleteQuery'
import { IAutocompleteBaseOption } from 'transfix-ui/components/AutocompleteBase/types'
import { useSearchVendorsQuery } from './graphql/SearchVendors'

export interface IVendorAutocompleteProps {
  AutocompleteProps?: Omit<
    React.ComponentProps<typeof AutocompleteBase>,
    'options' | 'defaultValue' | 'onChange' | 'value'
  >
  dataTest?: string
  excludedVendorIds?: string[]
  value: string
  onChange: (newVal: string | null, vendorId: string | null) => void
}

const getVendorId = (options: IAutocompleteBaseOption[], value: string): string | null => {
  if (!value) return null

  const option = options.find(({ text }) => text === value)
  return option?.id || null
}

export const VendorAutocomplete: FC<IVendorAutocompleteProps> = ({
  AutocompleteProps = {},
  onChange,
  value,
  dataTest,
  excludedVendorIds,
}) => {
  const { options = [], refetch } = useAutocompleteQuery(
    useSearchVendorsQuery,
    {
      variables: { searchTerm: value || '' },
      fetchPolicy: 'network-only',
    },
    {
      getOptionsConfig: data => {
        const { vendors: result } = data || {}
        const { nodes } = result || {}
        // If given vendor ids to exclude, filter out from options
        const vendors = excludedVendorIds
          ? compact(nodes).filter(vendor => !excludedVendorIds.includes(vendor.id))
          : compact(nodes)

        return {
          options: vendors,
          groupIndex: 1,
          mapOption: vendor => ({ id: vendor.id, text: vendor.name }),
        }
      },
    }
  )

  /**
   * Refetch when the input value changes
   */
  useDebounce(
    () => {
      if (value) {
        refetch({ searchTerm: value || '' })
      }
    },
    250,
    [value]
  )

  const inputHandler: TInputChangeHandler = (event, selectedItem) => {
    if (event !== null) {
      const vendorId = getVendorId(options, selectedItem)
      onChange(selectedItem, vendorId)
    }
  }
  const isOptionEqualToValue = ({ text }: IAutocompleteBaseOption) => text === value

  return (
    <AutocompleteBase
      {...AutocompleteProps}
      clearOnBlur={false}
      autoSelect={false}
      options={options}
      data-test={dataTest}
      onInputChange={inputHandler}
      isOptionEqualToValue={isOptionEqualToValue}
      inputValue={value ?? ''}
    />
  )
}

export default VendorAutocomplete
